import { Location } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentStateService } from '@ddv/behaviors';
import {
    AlertService,
    ConfirmationPopupService,
    ModalDialogRef,
    ModalDialogService,
    MultiSubscriptionComponent,
} from '@ddv/common-components';
import { DashboardFiltersComponent } from '@ddv/dashboards';
import { FeatureFlagService, UserData, UserEntitlements, UserService } from '@ddv/entitlements';
import { ManagerService } from '@ddv/layout';
import {
    AdditionalFilter,
    AppWidgetState,
    Client,
    DashboardFilter,
    FilterPreference,
    FilterQueryParams,
    Fund,
    FuzzyDates,
    getGlobalSaveDialogOptions,
    isInvestorQueryType,
    MANAGE_WIDGET_ID,
    NamedQuery,
    UserPreferences,
    VisualizationConfigs,
    VisualizationType,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { FuzzyDatesService } from '@ddv/reference-data';
import { deepClone } from '@ddv/utils';
import {
    SaveWidgetFormComponent,
    WidgetService,
    WidgetsStateRelayService,
    LegacyWidgetsService,
    WidgetsService,
    removeTradeReleaseComment,
} from '@ddv/widgets';
import { DropdownOption } from '@hs/ui-core-common';
import { FuzzyDate as CcFuzzyDate } from '@hs/ui-core-date';
import { Fund as CcFund } from '@hs/ui-core-fund';
import { Subscription, switchMap } from 'rxjs';

import { MWLeftNavComponent } from '../left-nav/manage-widgets-left-nav/manage-widgets-left-nav.component';

@Component({
    selector: 'app-mw-dashboard',
    templateUrl: './manage-widgets-dashboard.component.html',
    styleUrls: ['./manage-widgets-dashboard.component.scss'],
    standalone: false,
})
export class ManageWidgetsDashboardComponent extends MultiSubscriptionComponent implements OnInit, OnChanges {
    @Input() clientCode: string | undefined;
    @Input() navRef: MWLeftNavComponent | undefined;
    @Input() namedQuery: NamedQuery | undefined;
    @Input() autoAddDynamicColumnsVisualizations: Map<string, boolean> | undefined;
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;
    @Input() clients: Client[] = [];
    @Input() ccFuzzyDates: CcFuzzyDate[] = [];
    @Input() funds: CcFund[] = [];
    @Input() userPreferences: UserPreferences | undefined;

    @Output() revertChanges = new EventEmitter();

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly alertService = inject(AlertService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly communicationService = inject(WidgetsStateRelayService);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly fuzzyDatesService = inject(FuzzyDatesService);
    private readonly legacyWidgetsService = inject(LegacyWidgetsService);
    private readonly location = inject(Location);
    private readonly managerService = inject(ManagerService);
    private readonly modalService = inject(ModalDialogService);
    private readonly namedQueriesService = inject(NamedQueriesService);
    private readonly router = inject(Router);
    private readonly widgetService = inject(WidgetService);
    private readonly widgetsService = inject(WidgetsService);
    private readonly userService = inject(UserService);

    protected fuzzyDates: FuzzyDates = new FuzzyDates();
    protected queryParams: FilterQueryParams | undefined;
    protected filters: FilterPreference = {};
    protected nonUdfFilters: AdditionalFilter[] = [];
    @ViewChild('dashboardFilters', { static: true }) queryParamsRef: DashboardFiltersComponent | undefined;
    widgetInfo: AppWidgetState | undefined;
    manageWidgetId = MANAGE_WIDGET_ID;
    enableSave = false;
    saveWidgetDialogRef: ModalDialogRef<void, SaveWidgetFormComponent> | undefined;
    isMultiClient = false;
    userSelectedDate: { dateFrom: string, dateTo: string } | undefined;
    isGlobalEditPartial = false;

    private isStackedQuery = false;
    private queryTypeName: string | undefined;
    private namedQuerySubscription: Subscription | undefined;

    ngOnInit(): void {
        this.initInfo();

        this.userSelectedDate = { dateFrom: this.userPreferences?.startDate ?? '', dateTo: this.userPreferences?.endDate ?? '' };

        this.subscribeTo(this.currentStateService.isMultiClient$, (isMultiClient: boolean) => {
            this.isMultiClient = isMultiClient;
        });

        this.communicationService.enableWidgetSaveObs
            .subscribe({
                next: (status) => this.updateSaveBtnStatus(status),
            });

        this.communicationService.onWidgetInfoClearedObs
            .subscribe({
                next: (isWidgetFromFinder: boolean) => this.clearAll(isWidgetFromFinder),
            });

        this.communicationService.updateWidgetInfoObs
            .subscribe({
                next: (info) => this.updateInfo(info),
            });

        this.communicationService.widgetDimensionObs
            .subscribe({
                next: (info) => {
                    if (this.widgetInfo) {
                        this.widgetInfo.widgetPosition = info.widgetPosition;
                    }
                },
            });

        this.communicationService.updateVizConfigObs
            .subscribe({
                next: (config) => this.updateVizConfig(config),
            });

        this.isGlobalEditPartial = !!this.entitlements?.hasGlobalEditPartial();

        this.fuzzyDatesService.fuzzyDates()
            .subscribe({
                next: (fuzzyDates) => this.fuzzyDates = fuzzyDates,
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.namedQuery?.currentValue?.id) {
            if (this.namedQuerySubscription) {
                this.namedQuerySubscription.unsubscribe();
            }

            this.subscribeToNamedQuery();
        }

        if (changes.autoAddDynamicColumnsVisualizations && !changes.autoAddDynamicColumnsVisualizations.firstChange) {
            this.updateSaveBtnStatus(true);
        }
    }

    setNavRef(mwNavComponent: MWLeftNavComponent): void {
        this.navRef = mwNavComponent;
    }

    saveWidget(): void {
        if (this.validateUserConfiguration()) {
            if (this.widgetInfo?.isGlobal && !this.isGlobalEditPartial) {
                const confirmDialogOptions = getGlobalSaveDialogOptions();
                this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
                    next: (action) => {
                        if (action === 'confirm') {
                            this.saveWidgetInfo(this.widgetInfo);
                        }
                    },
                });
            } else if (this.widgetInfo?.id) {
                this.saveWidgetInfo(this.widgetInfo);
            } else {
                this.openSaveDialog({ isActiveDate: this.widgetInfo?.isActiveDate, clientCode: this.clientCode } as AppWidgetState);
            }
        }
    }

    showSaveDialog(isCopy = false): void {
        if (this.validateUserConfiguration()) {
            const info = deepClone(this.widgetInfo);
            if (isCopy && info) {
                info.name = `${this.widgetInfo?.name} Copy`;
                info.id = undefined;
                info.sharing = { toUsers: [], toTeams: [] };
                info.isShared = false;
                info.description = '';
                info.isGlobal = false;
            }
            this.openSaveDialog(info, isCopy, info?.isGlobal);
        }
    }

    onApplySelectedFunds(ccFunds: CcFund[]): void {
        const funds = this.funds.filter((fund) => ccFunds.some((ccFund) => ccFund.code === fund.code));

        if (this.widgetInfo?.widgetFilters) {
            this.widgetInfo.widgetFilters.funds = funds.map((fund) => new Fund(fund, this.userPreferences?.fundNamesSource ?? ''));
        }
        this.updateCoreWidgetQueryParams();
        this.updateFuzzyDatesForInvestor(funds);
    }

    onApplySelectedClients(clients: Client[]): void {
        if (this.widgetInfo?.widgetFilters) {
            this.widgetInfo.widgetFilters.clients = clients;
        }
        this.updateCoreWidgetQueryParams();
    }

    onApplyAdditionalFilters(filters: DashboardFilter[]): void {
        if (this.widgetInfo?.widgetFilters) {
            this.widgetInfo.widgetFilters.filters = filters;
        }
        this.updateCoreWidgetQueryParams();
    }

    updateCoreWidgetQueryParams(isWidgetFromFinder?: boolean): void {
        // This updates the widget filters in the live-demo-widget
        this.communicationService.setCurrentQueryParams(this.widgetInfo?.widgetFilters);
        if (!isWidgetFromFinder) {
            this.managerService.sendMessageToAllWidgetsOnWorkspace(0, {
                action: 'UPDATE_WIDGET_FILTERS',
                filters: this.widgetInfo?.widgetFilters,
            });
        }

        this.filters = this.widgetInfo?.widgetFilters ?? {};
        this.nonUdfFilters = this.filters.filters?.filter((f) => !f.name?.startsWith('udf_')) ?? [];

        if (this.navRef && this.navRef.selectedWidgets.length !== 0) {
            this.updateSaveBtnStatus(true);
        }
    }

    updateWidgetName(): void {
        this.managerService.sendMessageToAllWidgetsOnWorkspace(0, {
            action: 'UPDATE_WIDGET_TITLE',
            titleInfo: {
                name: this.widgetInfo?.name,
                displayNameType: this.widgetInfo?.displayNameType,
                customDisplayName: this.widgetInfo?.customDisplayName,
            },
        });
    }

    clearAll(isWidgetFromFinder: boolean): void {
        this.initInfo();
        this.updateCoreWidgetQueryParams(isWidgetFromFinder);
        this.updateWidgetName();
        this.updateSaveBtnStatus(false);
    }

    updateVizConfig(config: VisualizationConfigs): void {
        // eslint-disable-next-line eqeqeq
        if (this.widgetInfo?.isActiveDate === null) {
            this.updateIsActiveDate(config.visualizationType);
        }
    }

    updateIsActiveDate(vizType: VisualizationType): void {
        if (this.widgetInfo) {
            this.widgetInfo.isActiveDate = this.widgetService.getDefaultIsActiveDate(vizType);
        }
    }

    refreshCoreWidget(id: number): void {
        this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(switchMap((isFlagEnabled) => {
                if (isFlagEnabled) {
                    return this.widgetsService.fetchWidgetInfo(id, this.isMultiClient, this.clientCode ?? '');
                }
                return this.legacyWidgetsService.fetchWidgetInfo(id);
            }))
            .subscribe({
                next: (widgetState: AppWidgetState) => {
                    removeTradeReleaseComment(widgetState, this.entitlements?.getCostCenter());
                    this.navRef?.onEditBaseWidget(widgetState);
                },
            });
    }

    validateUserConfiguration(): boolean {
        let allValid = true;
        let msg = 'Please complete the configuration for -';
        this.navRef?.validateWidgetConfigs().forEach((item) => {
            if (!item.isValid) {
                allValid = false;
                msg += ` ${item.name} to save the widget.`;
            }
        });
        if (!allValid) {
            this.alertService.warn(msg);
        }
        return allValid;
    }

    protected onApplyDateChanges(filterDate: FilterQueryParams): void {
        Object.keys(filterDate).forEach((key) => {
            if (this.widgetInfo?.widgetFilters) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this.widgetInfo.widgetFilters as any)[key] = this.isStackedQuery ? undefined : (filterDate as any)[key];
            }
        });

        if (this.widgetInfo) {
            // eslint-disable-next-line eqeqeq
            this.widgetInfo.isActiveDate = filterDate.activeDate !== null;
        }
        this.updateCoreWidgetQueryParams();
    }

    protected onApplyAdditionalFilterChanges(additionalFilters: Record<string, DropdownOption[]>[] | undefined): void {
        if (this.widgetInfo?.widgetFilters) {
            this.widgetInfo.widgetFilters.additionalFilters = additionalFilters;
        }
        this.updateCoreWidgetQueryParams();
    }

    private updateInfo(info: AppWidgetState): void {
        this.widgetInfo = info;
        this.updateVizConfig(info.visualizationConfigs[0]);
        this.communicationService.setCurrentQueryParams(this.widgetInfo.widgetFilters);

        this.filters = this.widgetInfo?.widgetFilters ?? {};
        this.nonUdfFilters = this.filters.filters?.filter((f) => !f.name?.startsWith('udf_')) ?? [];

        if (this.navRef && this.navRef.selectedWidgets.length !== 0) {
            this.updateSaveBtnStatus(true);
        }
        this.updateWidgetName();
        this.updateSaveBtnStatus(false);
    }

    private initInfo(): void {
        this.widgetInfo = this.widgetService.getDefaultWidgetState();
    }

    private openSaveDialog(info: AppWidgetState | undefined, isCopy = false, isGlobal = false): void {
        this.saveWidgetDialogRef = this.modalService.open<void, SaveWidgetFormComponent>(SaveWidgetFormComponent, { windowClass: 'save-dialog' });
        const saveWidgetForm = this.saveWidgetDialogRef.componentInstance;
        saveWidgetForm.userPreferences = this.userPreferences;
        saveWidgetForm.formData = info;
        saveWidgetForm.isCopy = isCopy;
        saveWidgetForm.isGlobal = isGlobal;
        saveWidgetForm.coreWidgetType = this.navRef?.getUserSelectedConfigs().coreWidgetType;
        saveWidgetForm.sharing = deepClone(info?.sharing) ?? { toUsers: [], toTeams: [] };
        saveWidgetForm.userPreferences = this.userPreferences;
        saveWidgetForm.entitlements = this.entitlements;
        saveWidgetForm.user = this.user;
        saveWidgetForm.clientCode = this.clientCode ?? '';

        if (this.isStackedQuery) {
            saveWidgetForm.isStackedQuery = this.isStackedQuery;
        }

        saveWidgetForm.widgetSaveFormSubmit.subscribe({
            next: (widgetData: AppWidgetState) => this.saveWidgetInfo(widgetData),
        });
    }

    private removeSaveDialog(): void {
        if (!this.saveWidgetDialogRef) {
            return;
        }
        this.saveWidgetDialogRef.close();
        this.saveWidgetDialogRef = undefined;
    }

    private saveWidgetInfo(info: AppWidgetState | undefined): void {
        this.updateSaveBtnStatus(false);

        const widgetInfo = this.getWidgetInfo(info);
        const coreWidgetConfig = this.getCoreWidgetConfig(widgetInfo);
        this.setCoreWidgetConfigFilters(coreWidgetConfig);
        coreWidgetConfig.visualizationConfigs = this.getCoreWidgetConfigVisualizationConfigs(coreWidgetConfig);
        if (coreWidgetConfig.visualizationConfigs.length) {
            this.setCoreWidgetConfigSort(coreWidgetConfig);
        }
        coreWidgetConfig.namedQueryId = coreWidgetConfig.namedQuery?.id ?? '';

        this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        if (!coreWidgetConfig.id) {
                            return this.widgetsService.createCoreWidget(coreWidgetConfig, this.isMultiClient, this.clientCode ?? '');
                        }
                        return this.widgetsService.updateCoreWidget(coreWidgetConfig, this.isMultiClient, this.clientCode ?? '');
                    }
                    return this.legacyWidgetsService.saveCoreWidget(coreWidgetConfig, this.isMultiClient);
                }),
            )
            .subscribe({
                next: (response) => this.onSaveCoreWidgetSuccess(response),
                error: (error) => this.onSaveCoreWidgetError(error._body, coreWidgetConfig),
            });
    }

    private updateSaveBtnStatus(status = true): void {
        const userPermission = !!this.widgetInfo?.sharing.toUsers
            .some((user) => user.permission === 'write' && user.name.toLocaleLowerCase() === this.userPreferences?.username.toLocaleLowerCase());

        this.enableSave = !!(this.widgetInfo?.privilege ?
            ((this.widgetInfo.privilege === 'EDIT' || userPermission) && status) :
            (status));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(this.cdr as any).destroyed) {
            this.cdr.detectChanges();
        }
    }

    private getWidgetInfo(widgetInfo?: AppWidgetState): AppWidgetState {
        return Object.assign(this.widgetService.getDefaultWidgetState(), widgetInfo ?? this.widgetInfo);
    }

    private getCoreWidgetConfig(widgetInfo: AppWidgetState): AppWidgetState {
        return new AppWidgetState(deepClone({
            ...widgetInfo,
            id: widgetInfo.id,
            widgetFilters: this.queryParamsRef?.queryParams,
            widgetPosition: this.widgetInfo?.widgetPosition,
            isActiveDate: this.widgetInfo?.isActiveDate,
            version: widgetInfo.version,
            ...this.navRef?.getUserSelectedConfigs(),
        } as AppWidgetState));
    }

    private setCoreWidgetConfigFilters(coreWidgetConfig: AppWidgetState): void {
        if (!coreWidgetConfig.widgetFilters) {
            return;
        }
        if (coreWidgetConfig.widgetFilters.filters) {
            coreWidgetConfig.widgetFilters.filters.forEach((filter) => {
                filter.filterValuesType = this.getFilterValuesType(filter.values ?? []);
            });
        }
    }

    private getCoreWidgetConfigVisualizationConfigs(coreWidgetConfig: AppWidgetState): VisualizationConfigs[] {
        const vizIds = this.widgetInfo?.visualizationConfigs.reduce((accum, config) => {
            accum[config.visualizationType] = config;
            return accum;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }, {} as any);

        return coreWidgetConfig.visualizationConfigs.reduce((updatedConfigs, vs) => {
            let updatedVizConfig = vs;
            // The first config is default
            updatedVizConfig.isDefault = !updatedConfigs.length;

            if (vizIds[updatedVizConfig.visualizationType]) {
                updatedVizConfig.id = vizIds[updatedVizConfig.visualizationType].id;
                // Get values from the visualization configs
                updatedVizConfig = Object.assign(vizIds[updatedVizConfig.visualizationType], vs);
            }

            if (updatedVizConfig.configs?.slicers) {
                updatedVizConfig.configs.slicers.forEach((slicer) => slicer.isDefault = false);
            }

            if (updatedVizConfig.visualizationType === 'SIMPLE_GRID' || updatedVizConfig.visualizationType === 'ADVANCED_GRID') {
                this.setAutoAddDynamicColumns(updatedVizConfig);
            }

            updatedConfigs.push(updatedVizConfig);

            return updatedConfigs;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }, [] as any[]);
    }

    private setAutoAddDynamicColumns(updatedVizConfig: VisualizationConfigs): void {
        if (this.autoAddDynamicColumnsVisualizations?.size) {
            const autoAddDynamicColumns = this.autoAddDynamicColumnsVisualizations.get(updatedVizConfig.visualizationType);
            if (autoAddDynamicColumns !== updatedVizConfig.autoAddDynamicColumns) {
                updatedVizConfig.autoAddDynamicColumns = autoAddDynamicColumns;
            }
        } else {
            updatedVizConfig.autoAddDynamicColumns ??= false;
        }
    }

    private setCoreWidgetConfigSort(coreWidgetConfig: AppWidgetState): void {
        coreWidgetConfig.tableSortDirection = coreWidgetConfig.visualizationConfigs[0].tableSortDirection;
        coreWidgetConfig.sortTableBy = coreWidgetConfig.visualizationConfigs[0].sortTableBy;

        coreWidgetConfig.visualizationConfigs.forEach((vc) => {
            if (vc.configs?.slicers?.length) {
                vc.configs.slicers.forEach((slicer) => {
                    if (slicer.configCustomSortOrder && typeof slicer.configCustomSortOrder !== 'string') {
                        slicer.configCustomSortOrder =
                            JSON.stringify(slicer.configCustomSortOrder.length ? slicer.configCustomSortOrder : []);
                    }

                    if (slicer.colorType !== 'MONOCHROMATIC' || vc.tableSortDirection === 'CUSTOM') {
                        slicer.colorSortBy = undefined;
                        slicer.colorSortDirection = undefined;
                    }
                });
            }
        });
    }

    private updateFuzzyDatesForInvestor(funds: CcFund[]): void {
        if (isInvestorQueryType(this.queryTypeName)) {
            const updatedFunds = funds.map((fund) => ({ code: fund.code }));
            const queriesIds = [this.widgetInfo?.namedQueryId];
            this.fuzzyDatesService.pushMostRecentFuzzyDatesForInvestor(updatedFunds, queriesIds).subscribe();
        }
    }

    private getFilterValuesType(filterValues: (string | number | boolean)[]): string | undefined {
        const allValuesTypes: string[] = filterValues?.map((value) => {
            if (typeof value === 'object' && value == null) {
                return 'null';
            }

            return typeof value;
        });

        const valuesTypes = Array.from(new Set(allValuesTypes));
        const type = valuesTypes.length === 1 ?
            valuesTypes[0] :
            valuesTypes.length === 2 ? valuesTypes.find((valuesType) => valuesType !== 'null') : '';

        return type;
    }

    private onSaveCoreWidgetSuccess(response: AppWidgetState): void {
        this.widgetInfo = response;
        this.updateWidgetName();
        this.removeSaveDialog();
        const widgets = this.managerService.getWidgetIdsForWorkspace();
        widgets.forEach((widgetId) => {
            const prefs = this.managerService.getWidgetPreferences(widgetId);
            if (prefs && prefs.id === response.id) {
                this.managerService.setWidgetExtraPreferences(widgetId, prefs);
                this.managerService.setWidgetExtraReload(widgetId, true);
            }
        });
        const url = this.router.createUrlTree(['..', this.widgetInfo.id], { relativeTo: this.activatedRoute }).toString();
        this.location.replaceState(url);
    }

    private onSaveCoreWidgetError(error: { message: string } | string, coreWidgetConfig: AppWidgetState): void {
        if (error) {
            const errorBody = typeof error === 'string' ? JSON.parse(error) : error;

            if (errorBody.message === 'Widget name is already used') {
                this.saveWidgetDialogRef?.componentInstance.saveWidgetForm?.get('name')?.setErrors({ isDuplicate: true });
                if (this.saveWidgetDialogRef) {
                    this.saveWidgetDialogRef.componentInstance.isSaveInvoked = false;
                }
                this.updateSaveBtnStatus(true);
            } else if (errorBody.message === 'Widget version is stale') {
                this.handleStaleWidgetVersion(errorBody.message, coreWidgetConfig.id ?? 0);
            }
        }
    }

    private handleStaleWidgetVersion(message: string, id: number): void {
        this.managerService.sendMessageToAllWidgetsOnWorkspace(0, { action: 'RESET_VIEW' });
        this.removeSaveDialog();
        this.confirmationService.showConfirmationPopup({ message }, true).subscribe({
            next: (action) => {
                if (action === 'confirm') {
                    this.refreshCoreWidget(id);
                }
            },
        });
    }

    private subscribeToNamedQuery(): void {
        if (!this.namedQuery?.id) {
            return;
        }

        this.namedQuerySubscription = this.namedQueriesService.fetchNamedQuery(this.namedQuery.id).subscribe({
            next: (namedQuery: NamedQuery | undefined) => {
                this.queryTypeName = namedQuery?.normalizeQueryTypeName();
                this.isStackedQuery = !!namedQuery?.normalizeQueryIsStacked();
            },
        });
    }
}
